import { useState } from 'react'
import { cloneDeep } from 'lodash'
import { store } from 'redux/store'

import { useActionless, useActions, useAppSelector } from 'redux/hooks'
import { toggleModal as toggleModalAction } from 'redux/active/actions'
import { setDeckStateAction } from 'redux/deck/actions'
import { SET_ACTIVE_STATE } from 'redux/active/actions/types'
import { useCanEditDeck } from 'redux/deck/selectors'

import SaveService from 'services/save.service'
import PinService from 'services/pin.service'
import ToastService from 'services/toast.service'

import { DeckCardDragType } from 'types/draggables'
import { CardType, STACK, OWNED } from 'types/deck'

import Icon from 'components/elements/Icon'
import DropZones from 'components/deckPage/dragAndDrop/DropZones'

import { getMultiSelectedUpdates, getNewCategories, getMultiSelectedPinUpdates } from 'utils/CategoryChangeHelpers'

import styles from './fixedDropZones.module.scss'

const FixedDropZones = () => {
  const [isDragging, setIsDragging] = useState(false)

  const canEditDeck = useCanEditDeck()

  const [setActiveState] = useActionless(SET_ACTIVE_STATE)
  const [toggleModal] = useActions(toggleModalAction)
  const [setDeckState] = useActions(setDeckStateAction)

  const moveToCategory = (
    card: CardType,
    currentCategory: string,
    newCategory: string | null,
    multiSelectedIds?: Record<string, boolean>,
  ) => {
    if (multiSelectedIds) {
      const updatedCards = getMultiSelectedUpdates(multiSelectedIds, currentCategory, newCategory)

      return SaveService.save(updatedCards).then(() => setActiveState({ multiSelectedIds: {} }))
    }

    const updatedCategory = newCategory || card.categories[1] || card.types[0] || card.front?.types[0] || '😎'

    let updatedCategories = getNewCategories(card.categories, STACK.CUSTOM, currentCategory, updatedCategory)

    if (!newCategory && card.globalCategories) updatedCategories = [...card.globalCategories]

    const updatedCard = { ...card, categories: updatedCategories, qty: card.qty || 1 }

    SaveService.save(updatedCard)
  }

  const handleMakeNewCategory = ({ card, stackName, multiSelectedIds }: DeckCardDragType) =>
    toggleModal('newCategoryDrop', { card, sourceStack: stackName, multiSelectedIds })

  const handlePinCards = (card: CardType, multiSelectedIds?: Record<string, boolean>) => {
    const { cardMap } = store.getState().deck

    let pinnedCards = [{ ...card, pinnedStatus: OWNED.YES }]
    let ids = [card.id]

    if (multiSelectedIds) {
      pinnedCards = getMultiSelectedPinUpdates(multiSelectedIds)
      ids = Object.keys(multiSelectedIds).filter(id => multiSelectedIds[id])
    }

    return PinService.add(pinnedCards)
      .then(() => {
        const updatedCardMap = cloneDeep(cardMap)

        for (const id of ids) {
          updatedCardMap[id].pinnedStatus = OWNED.YES
        }

        setDeckState({ cardMap: updatedCardMap })

        ToastService.create(`Pinned ${multiSelectedIds ? `${ids.length} cards` : card.name}`, 'Pinned Cards', 'success')
      })
      .catch(err => ToastService.create(err.error, 'Pinned Cards', 'error'))
  }

  // Note that we're only watching the first one of these for canDrop changes since they'd all be the same
  return (
    <div className={`${styles.dropZoneContainer} ${isDragging ? styles.dropZoneContainerIsDragging : ''}`}>
      <DropZones
        onCanDropChange={setIsDragging}
        onDrop={drag => moveToCategory(drag.card, drag.stackName || '😎', null, drag.multiSelectedIds)}
        checkCanDrop={() => canEditDeck}
        className={`${styles.dropZone} ${isDragging ? styles.dropZoneIsDragging : ''} `}
        isOverClassName={styles.isHovered}>
        <Icon name="add" />
        Auto
      </DropZones>
      <DropZones
        onDrop={handleMakeNewCategory}
        checkCanDrop={() => canEditDeck}
        className={`${styles.dropZone} ${isDragging ? styles.dropZoneIsDragging : ''} `}
        isOverClassName={styles.isHovered}>
        <Icon name="add" />
        New Category
      </DropZones>
      <DropZones
        onDrop={drag => moveToCategory(drag.card, drag.stackName || '😎', 'Maybeboard', drag.multiSelectedIds)}
        checkCanDrop={() => canEditDeck}
        className={`${styles.dropZone} ${isDragging ? styles.dropZoneIsDragging : ''} `}
        isOverClassName={styles.isHovered}>
        <Icon name="add" />
        Maybeboard
      </DropZones>
      <DropZones
        onDrop={drag => moveToCategory(drag.card, drag.stackName || '😎', 'Sideboard', drag.multiSelectedIds)}
        checkCanDrop={() => canEditDeck}
        className={`${styles.dropZone} ${isDragging ? styles.dropZoneIsDragging : ''} `}
        isOverClassName={styles.isHovered}>
        <Icon name="add" />
        Sideboard
      </DropZones>
      <DropZones
        onDrop={drag => handlePinCards(drag.card, drag.multiSelectedIds)}
        checkCanDrop={() => canEditDeck}
        className={`${styles.dropZone} ${isDragging ? styles.dropZoneIsDragging : ''} `}
        isOverClassName={styles.isHovered}>
        <Icon name="add" />
        Pinned Cards
      </DropZones>
    </div>
  )
}

export default FixedDropZones
